import React, { useEffect, useState } from "react";
import {
  Box, Grid, Card, CardContent, Typography, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, IconButton, Divider, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import adminApi from "../api/adminApi";

const STATUS_COLORS = {
  success: "success",
  failed: "error",
  payment_captured_pending_submission: "warning",
};

const StatCard = ({ label, value, color = "#1C2541" }) => (
  <Card elevation={1} sx={{ borderRadius: 3 }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" fontWeight="bold" color={color}>{value}</Typography>
    </CardContent>
  </Card>
);

const STATUS_LABELS = {
  success: "Confirmed",
  failed: "Failed",
  payment_captured_pending_submission: "Pending",
};

const DetailRow = ({ label, value }) =>
  value ? (
    <Box sx={{ display: "flex", gap: 2, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{value}</Typography>
    </Box>
  ) : null;

const ReservationDetail = ({ reservation: r, onClose }) => (
  <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      Reservation Details
      <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">Status</Typography>
          <Box mt={0.5}>
            <Chip
              label={STATUS_LABELS[r.status] ?? r.status}
              color={STATUS_COLORS[r.status] ?? "default"}
              size="small"
            />
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="overline" color="text.secondary">Customer</Typography>
          <DetailRow label="Name" value={r.customerName} />
          <DetailRow label="Email" value={r.customerEmail} />
          <DetailRow label="Phone" value={r.customerPhone} />
        </Box>

        <Divider />

        <Box>
          <Typography variant="overline" color="text.secondary">Vehicle</Typography>
          <DetailRow label="Model" value={r.vehicleModel} />
          <DetailRow label="Group / Code" value={r.vehicleCode} />
        </Box>

        <Divider />

        <Box>
          <Typography variant="overline" color="text.secondary">Dates & Locations</Typography>
          <DetailRow label="Pick-up" value={r.pickupDate ? `${r.pickupDate} ${r.pickupTime ?? ""}`.trim() : null} />
          <DetailRow label="Pick-up desk" value={r.pickUpDesk} />
          <DetailRow label="Return" value={r.returnDate ? `${r.returnDate} ${r.returnTime ?? ""}`.trim() : null} />
          <DetailRow label="Return desk" value={r.dropOffDesk} />
        </Box>

        <Divider />

        <Box>
          <Typography variant="overline" color="text.secondary">Payment</Typography>
          <DetailRow label="Total" value={r.totalRentalValue ? `€${r.totalRentalValue}` : null} />
          <DetailRow label="Payment type" value={r.paymentType} />
          <DetailRow label="Payment ID" value={r.paypalOrderId} />
          <DetailRow label="TRWS booking ID" value={r.trwsBookingId} />
        </Box>

        {r.errorMessage && (
          <>
            <Divider />
            <Box>
              <Typography variant="overline" color="error">Error</Typography>
              <Typography variant="body2" color="error.main" sx={{ mt: 0.5, wordBreak: "break-word" }}>
                {r.errorMessage}
              </Typography>
            </Box>
          </>
        )}

        <Divider />

        <DetailRow label="Created at" value={new Date(r.createdAt).toLocaleString()} />
      </Stack>
    </DialogContent>
  </Dialog>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);

  const load = async () => {
    setLoading(true);
    const [statsRes, listRes] = await Promise.all([
      adminApi.get("/reservations/stats"),
      adminApi.get(`/reservations${statusFilter ? `?status=${statusFilter}` : ""}`),
    ]);
    setStats(statsRes.data);
    setReservations(listRes.data.reservations);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter]);

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="#1C2541" mb={3}>Dashboard</Typography>

      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} md={3}><StatCard label="Total Bookings" value={stats.total} /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Confirmed" value={stats.success} color="#2e7d32" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Failed" value={stats.failed} color="#c62828" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Total Revenue" value={`€${stats.revenue.toFixed(2)}`} color="#EE8013" /></Grid>
      </Grid>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight="bold" color="#1C2541">Recent Bookings</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Filter by status</InputLabel>
          <Select value={statusFilter} label="Filter by status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="success">Confirmed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="payment_captured_pending_submission">Pending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Vehicle</strong></TableCell>
              <TableCell><strong>Pick-up</strong></TableCell>
              <TableCell><strong>Return</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservations.map((r) => (
              <TableRow key={r._id} hover sx={{ cursor: "pointer" }} onClick={() => setSelectedReservation(r)}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{r.customerName}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.customerEmail}</Typography>
                </TableCell>
                <TableCell>{r.vehicleModel || r.vehicleCode}</TableCell>
                <TableCell>
                  <Typography variant="body2">{r.pickupDate}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.pickUpDesk}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.returnDate}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.dropOffDesk}</Typography>
                </TableCell>
                <TableCell>€{r.totalRentalValue}</TableCell>
                <TableCell>
                  <Chip label={r.status === "success" ? "Confirmed" : r.status === "failed" ? "Failed" : "Pending"}
                    color={STATUS_COLORS[r.status]} size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{new Date(r.createdAt).toLocaleDateString()}</Typography>
                </TableCell>
              </TableRow>
            ))}
            {reservations.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary" py={3}>No bookings found</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedReservation && (
        <ReservationDetail
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
        />
      )}
    </Box>
  );
};

export default Dashboard;
